import type { RouteObject } from "react-router-dom";
import { lazy } from "react";
import { Navigate } from "react-router-dom";
import NotFound from "../pages/NotFound";

// Lazy-load every page so visitors only download the code for the page they
// actually open. This keeps the initial bundle small and the site fast.
const Home = lazy(() => import("../pages/home/page"));
const Sales = lazy(() => import("../pages/sales/page"));
const PropertyDetail = lazy(() => import("../pages/property-detail/page"));
const About = lazy(() => import("../pages/about/page"));
const Contact = lazy(() => import("../pages/contact/page"));
const Login = lazy(() => import("../pages/login/page"));
const Register = lazy(() => import("../pages/register/page"));
const Sellers = lazy(() => import("../pages/sellers/page"));
const Buyers = lazy(() => import("../pages/buyers/page"));
const Team = lazy(() => import("../pages/team/page"));
const TeamMember = lazy(() => import("../pages/team/member"));
const Reviews = lazy(() => import("../pages/reviews/page"));
const Sold = lazy(() => import("../pages/sold/page"));
const Neighborhoods = lazy(() => import("../pages/neighborhoods/page"));
const NeighborhoodDetail = lazy(() => import("../pages/neighborhoods/detail"));
const HomeValue = lazy(() => import("../pages/home-value/page"));
const AIChat = lazy(() => import("../pages/ai-chat/page"));
const MortgageCalculator = lazy(() => import("../pages/mortgage-calculator/page"));
const ClosingCosts = lazy(() => import("../pages/closing-costs/page"));
const AdminDashboard = lazy(() => import("../pages/portal/admin/page"));
const AdminListings = lazy(() => import("../pages/portal/admin/listings"));
const AdminLeads = lazy(() => import("../pages/portal/admin/leads"));
const AdminOffers = lazy(() => import("../pages/portal/admin/offers"));
const AdminValuations = lazy(() => import("../pages/portal/admin/valuations"));
const AdminReimagine = lazy(() => import("../pages/portal/admin/reimagine"));
const AdminMls = lazy(() => import("../pages/portal/admin/mls"));
const AdminAgents = lazy(() => import("../pages/portal/admin/agents"));
const AdminReviews = lazy(() => import("../pages/portal/admin/reviews"));
const AdminNeighborhoods = lazy(() => import("../pages/portal/admin/neighborhoods"));
const AdminPhone = lazy(() => import("../pages/portal/admin/phone"));
const AdminCalls = lazy(() => import("../pages/portal/admin/calls"));
const AdminCallFlow = lazy(() => import("../pages/portal/admin/callflow"));

const routes: RouteObject[] = [
  // ---------------------------------------------------------------------------
  // Public brokerage site (sales-only)
  // ---------------------------------------------------------------------------
  { path: "/", element: <Home /> },
  { path: "/sales", element: <Sales /> },
  // Unified listing search reuses the Sales listing page for now.
  { path: "/search", element: <Sales /> },
  { path: "/property/:id", element: <PropertyDetail /> },
  { path: "/sellers", element: <Sellers /> },
  { path: "/buyers", element: <Buyers /> },
  { path: "/team", element: <Team /> },
  { path: "/team/:slug", element: <TeamMember /> },
  { path: "/reviews", element: <Reviews /> },
  { path: "/sold", element: <Sold /> },
  { path: "/neighborhoods", element: <Neighborhoods /> },
  { path: "/neighborhoods/:slug", element: <NeighborhoodDetail /> },
  { path: "/home-value", element: <HomeValue /> },
  { path: "/ai-chat", element: <AIChat /> },
  { path: "/mortgage-calculator", element: <MortgageCalculator /> },
  { path: "/closing-costs", element: <ClosingCosts /> },
  { path: "/about", element: <About /> },
  { path: "/contact", element: <Contact /> },
  { path: "/login", element: <Login /> },
  { path: "/register", element: <Register /> },

  // ---------------------------------------------------------------------------
  // Brokerage admin portal
  // ---------------------------------------------------------------------------
  { path: "/portal/admin", element: <AdminDashboard /> },
  { path: "/portal/admin/listings", element: <AdminListings /> },
  { path: "/portal/admin/leads", element: <AdminLeads /> },
  { path: "/portal/admin/offers", element: <AdminOffers /> },
  { path: "/portal/admin/valuations", element: <AdminValuations /> },
  { path: "/portal/admin/reimagine", element: <AdminReimagine /> },
  { path: "/portal/admin/mls", element: <AdminMls /> },
  { path: "/portal/admin/agents", element: <AdminAgents /> },
  { path: "/portal/admin/reviews", element: <AdminReviews /> },
  { path: "/portal/admin/neighborhoods", element: <AdminNeighborhoods /> },
  { path: "/portal/admin/phone", element: <AdminPhone /> },
  { path: "/portal/admin/calls", element: <AdminCalls /> },
  { path: "/portal/admin/call-flow", element: <AdminCallFlow /> },
  { path: "/portal", element: <Navigate to="/portal/admin" replace /> },

  // ---------------------------------------------------------------------------
  { path: "*", element: <NotFound /> },
];

export default routes;